import type { CameraActivityMetric } from '../../services/cameraActivityMeasurementApi';
import type { CameraMeasurementWindowPreset } from './cameraMeasurementPresentation';

export const CAMERA_MEASUREMENT_DURATION_GRAPH_KIND = 'independent-horizontal-bars';

export const CAMERA_MEASUREMENT_DURATION_OVERLAP_HELPER =
  'Durations are independent. Simultaneous presence is time when both a pedestrian and forklift were present in the camera view.';

const MINUTE = 60;
const HOUR = 3600;

export function cameraMeasurementWindowSeconds(args: {
  start?: string | null;
  end?: string | null;
  preset?: CameraMeasurementWindowPreset | null;
}): number {
  const start = Date.parse(String(args.start || ''));
  const end = Date.parse(String(args.end || ''));
  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    return (end - start) / 1000;
  }
  if (args.preset === 'last_complete_15m') return 15 * MINUTE;
  if (args.preset === 'last_complete_24h') return 24 * HOUR;
  if (args.preset === 'last_complete_hour') return HOUR;
  return 0;
}

export function cameraMeasurementDurationChartTitle(preset: CameraMeasurementWindowPreset): string {
  if (preset === 'last_complete_15m') return 'Duration in last complete 15 min';
  if (preset === 'last_complete_24h') return 'Duration in last complete 24 hours';
  return 'Duration in last complete hour';
}

export function cameraMeasurementBarPercent(
  seconds: number | null,
  windowSeconds: number,
): { percent: number; overflow: boolean } | null {
  if (seconds == null || !Number.isFinite(seconds) || windowSeconds <= 0) return null;
  const value = Math.max(0, seconds);
  const overflow = value > windowSeconds + 0.5;
  const percent = Math.min(100, (value / windowSeconds) * 100);
  return { percent, overflow };
}

export function cameraMeasurementShowsBar(metric: CameraActivityMetric): boolean {
  if (metric.state === 'UNSUPPORTED' || metric.state === 'NOT_MEASURED') return false;
  return metric.seconds != null;
}

export function cameraMeasurementAxisTicks(windowSeconds: number): Array<{ seconds: number; label: string }> {
  if (!(windowSeconds > 0)) return [];
  if (windowSeconds <= 15 * MINUTE + 5) {
    return [0, 5, 10, 15].map((minutes) => ({
      seconds: Math.min(windowSeconds, minutes * MINUTE),
      label: minutes === 0 ? '0 min' : `${minutes} min`,
    }));
  }
  if (windowSeconds <= HOUR + 5) {
    return [
      { seconds: 0, label: '0' },
      { seconds: Math.min(windowSeconds, 15 * MINUTE), label: '15 min' },
      { seconds: Math.min(windowSeconds, 30 * MINUTE), label: '30 min' },
      { seconds: Math.min(windowSeconds, 45 * MINUTE), label: '45 min' },
      { seconds: windowSeconds, label: '1 hr' },
    ];
  }
  const hours = windowSeconds / HOUR;
  const marks = [0, 6, 12, 18, 24].filter((hour) => hour <= hours + 0.01);
  if (!marks.includes(hours) && hours > 0) marks.push(hours);
  return marks.map((hour) => ({
    seconds: Math.min(windowSeconds, hour * HOUR),
    label: hour === 0 ? '0' : `${Number.isInteger(hour) ? hour : hour.toFixed(1)} hr`,
  }));
}
